import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Button,
  Slider,
  Avatar,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  IconButton,
  useMediaQuery,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import api from '../api/client';
import { useBranding } from '../theme/BrandColorContext';
import StyleValidator from '../components/domain/StyleValidator';

const INITIAL_SETTINGS = {
  orgName: 'Acme Corp',
  domain: 'acme.pl',
  timezone: 'Europe/Warsaw',
  language: 'pl',
  brandColor: '#6D55F7',
  accentColor: '#9DE94B',
  fontFamily: 'Inter, system-ui, sans-serif',
  fontSizeScale: 1,
  fontUrl: '',
  logo: '',
  slogan: '',
  showLogoInCampaigns: true,
  minTeamSize: 5,
  aliasMode: false,
  dataRetentionMonths: 12,
  emailNotifications: true,
  pushNotifications: true,
  slackNotifications: false,
  notificationFrequency: 'daily',
};

function SettingsSection({ title, children }) {
  const theme = useTheme();
  return (
    <Paper
      sx={{
        p: { xs: 2.5, sm: 3 },
        mb: 3,
        borderRadius: `${theme.tokens?.primitive?.radius?.card ?? 20}px`,
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: 'none',
      }}
    >
      <Typography variant="h6" sx={{ fontFamily: theme.typography.h1.fontFamily, mb: 2.5, fontSize: '1.1rem' }}>
        {title}
      </Typography>
      {children}
    </Paper>
  );
}

function Settings() {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { setBranding } = useBranding();
  const [settings, setSettings] = useState(INITIAL_SETTINGS);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [admins, setAdmins] = useState([]);
  const [styleImportUrl, setStyleImportUrl] = useState('');
  const [styleImportLoading, setStyleImportLoading] = useState(false);
  const [styleImportResult, setStyleImportResult] = useState(null);

  useEffect(() => {
    api.get('/api/tenants/me/users')
      .then((data) => {
        const users = data.users || data;
        if (Array.isArray(users)) {
          setAdmins(users
            .filter((u) => ['admin', 'hr', 'owner'].includes(u.role))
            .map((u) => ({ ...u, name: u.name || u.email || 'Użytkownik' }))
            .slice(0, 5));
        }
      })
      .catch(() => setAdmins([]));
  }, []);

  useEffect(() => {
    api.get('/api/tenants/me')
      .then((tenant) => {
        const config = typeof tenant.config === 'string' ? JSON.parse(tenant.config) : (tenant.config || {});
        const branding = typeof tenant.branding === 'string' ? JSON.parse(tenant.branding) : (tenant.branding || {});
        const toAbsoluteUrl = (value) => {
          if (!value) return value;
          if (value.startsWith('http')) return value;
          if (value.startsWith('/')) return `${import.meta.env.VITE_API_URL}${value}`;
          return value;
        };
        const primaryColor = branding.primaryColor || INITIAL_SETTINGS.brandColor;
        const accentColor = branding.accentColor || INITIAL_SETTINGS.accentColor;
        const fontFamily = branding.fontFamily || INITIAL_SETTINGS.fontFamily;
        setSettings((prev) => ({
          ...prev,
          orgName: tenant.name || prev.orgName,
          domain: tenant.domain || prev.domain,
          timezone: config.timezone || prev.timezone,
          language: config.language || prev.language,
          brandColor: primaryColor,
          accentColor,
          fontFamily,
          fontSizeScale: branding.fontSizeScale ?? prev.fontSizeScale,
          fontUrl: toAbsoluteUrl(branding.fontUrl) || prev.fontUrl,
          logo: toAbsoluteUrl(branding.logo) || prev.logo,
          slogan: branding.slogan || prev.slogan,
          showLogoInCampaigns: branding.showLogo ?? prev.showLogoInCampaigns,
          minTeamSize: config.limits?.minTeamSize || prev.minTeamSize,
          aliasMode: config.features?.aliasMode || prev.aliasMode,
          dataRetentionMonths: config.limits?.dataRetentionMonths || prev.dataRetentionMonths,
          emailNotifications: config.notifications?.email ?? prev.emailNotifications,
          pushNotifications: config.notifications?.push ?? prev.pushNotifications,
          slackNotifications: config.notifications?.slack ?? prev.slackNotifications,
          notificationFrequency: config.notifications?.frequency || prev.notificationFrequency,
        }));
        setBranding({
          primaryColor,
          accentColor,
          fontFamily,
          fontSizeScale: branding.fontSizeScale ?? 1,
          fontUrl: toAbsoluteUrl(branding.fontUrl),
          logo: toAbsoluteUrl(branding.logo),
          slogan: branding.slogan,
        });
      })
      .catch(() => {});
  }, [setBranding]);

  const handleChange = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setSettings((prev) => ({ ...prev, [field]: value }));
  };

  const handleStyleAutoFix = (patch) => {
    setSettings((prev) => ({ ...prev, ...patch }));
    setSnackbar({ open: true, message: 'Zastosowano auto-dopasowanie. Kliknij „Zapisz branding”, aby zapisać.', severity: 'info' });
  };

  const handleImportStyle = async () => {
    if (!styleImportUrl.trim()) return;
    let url = styleImportUrl.trim();
    if (!/^https?:\/\//i.test(url)) url = `https://${url}`;
    setStyleImportLoading(true);
    try {
      const result = await api.post('/api/tenants/me/branding/extract', { url });
      setStyleImportResult(result);
      setSnackbar({ open: true, message: 'Styl ze strony pobrany — zobacz sugestie.', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: `Błąd importu stylu: ${err.body?.error || err.message}`, severity: 'error' });
    } finally {
      setStyleImportLoading(false);
    }
  };

  const applyImportedStyle = async () => {
    if (!styleImportResult) return;
    const patch = {};
    if (styleImportResult.siteName) patch.orgName = styleImportResult.siteName;
    if (styleImportResult.colors?.[0]) patch.brandColor = styleImportResult.colors[0];
    if (styleImportResult.colors?.[1]) patch.accentColor = styleImportResult.colors[1];
    if (styleImportResult.fonts?.[0]) patch.fontFamily = styleImportResult.fonts[0];
    if (styleImportResult.logo) patch.logo = styleImportResult.logo.startsWith('http') ? styleImportResult.logo : `${import.meta.env.VITE_API_URL}${styleImportResult.logo}`;
    if (styleImportResult.fontCssUrl) {
      patch.fontUrl = `${import.meta.env.VITE_API_URL}${styleImportResult.fontCssUrl}`;
    } else if (styleImportResult.cssUrls?.[0]) {
      patch.fontUrl = `${import.meta.env.VITE_API_URL}/api/tenants/me/branding/proxy-css?url=${encodeURIComponent(styleImportResult.cssUrls[0])}`;
    }

    const nextSettings = { ...settings, ...patch };
    setSettings(nextSettings);
    setSaving(true);

    try {
      const payload = {
        name: nextSettings.orgName,
        branding: {
          primaryColor: nextSettings.brandColor,
          accentColor: nextSettings.accentColor,
          fontFamily: nextSettings.fontFamily,
          fontUrl: nextSettings.fontUrl,
          logo: nextSettings.logo,
          slogan: nextSettings.slogan,
          showLogo: nextSettings.showLogoInCampaigns,
        },
      };
      await api.put('/api/tenants/me', payload);
      setBranding(payload.branding);
      setSnackbar({ open: true, message: 'Styl i nazwa organizacji zostały zapisane automatycznie.', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: `Błąd zapisywania stylu: ${err.body?.error || err.message}`, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!['image/png', 'image/svg+xml', 'image/jpeg'].includes(file.type)) {
      setSnackbar({ open: true, message: 'Obsługiwane formaty: PNG, SVG, JPG', severity: 'warning' });
      return;
    }
    setSaving(true);
    const formData = new FormData();
    formData.append('logo', file);
    try {
      const data = await api.upload('/api/tenants/me/logo', formData);
      const logoUrl = data.url?.startsWith('http') ? data.url : `${import.meta.env.VITE_API_URL}${data.url}`;
      await api.put('/api/tenants/me', { branding: { logo: logoUrl } });
      setSettings((prev) => ({ ...prev, logo: logoUrl }));
      setBranding((prev) => ({ ...prev, logo: logoUrl }));
      setSnackbar({ open: true, message: 'Logo przesłane i zapisane', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: `Błąd uploadu: ${err.body?.error || err.message}`, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async (section) => {
    setSaving(true);
    try {
      let payload = {};
      if (section === 'org') {
        payload = {
          name: settings.orgName,
          domain: settings.domain,
          config: { timezone: settings.timezone, language: settings.language },
        };
      } else if (section === 'branding') {
        payload = {
          branding: {
            primaryColor: settings.brandColor,
            accentColor: settings.accentColor,
            fontFamily: settings.fontFamily,
            fontSizeScale: settings.fontSizeScale,
            fontUrl: settings.fontUrl,
            logo: settings.logo,
            slogan: settings.slogan,
            showLogo: settings.showLogoInCampaigns,
          },
        };
        setBranding({
          primaryColor: settings.brandColor,
          accentColor: settings.accentColor,
          fontFamily: settings.fontFamily,
          fontSizeScale: settings.fontSizeScale,
          fontUrl: settings.fontUrl,
          logo: settings.logo,
          slogan: settings.slogan,
        });
      } else if (section === 'privacy') {
        payload = { config: { features: { aliasMode: settings.aliasMode }, limits: { minTeamSize: settings.minTeamSize, dataRetentionMonths: settings.dataRetentionMonths } } };
      } else if (section === 'notifications') {
        payload = { config: { notifications: { email: settings.emailNotifications, push: settings.pushNotifications, slack: settings.slackNotifications, frequency: settings.notificationFrequency } } };
      }

      await api.put('/api/tenants/me', payload);
      setSnackbar({ open: true, message: 'Zapisano pomyślnie', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: `Błąd zapisu: ${err.body?.error || err.message}`, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 840, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontFamily: theme.typography.h1.fontFamily, fontSize: { xs: '1.5rem', sm: '2rem' }, mb: 0.5 }}>
          Ustawienia
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Konfiguracja organizacji i preferencje
        </Typography>
      </Box>

      {/* Organizacja */}
      <SettingsSection title="Organizacja">
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <TextField
            label="Nazwa organizacji"
            value={settings.orgName}
            onChange={handleChange('orgName')}
            fullWidth
            size="small"
          />
          <TextField
            label="Domena e-mail"
            value={settings.domain}
            onChange={handleChange('domain')}
            fullWidth
            size="small"
          />
          <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
            <FormControl size="small" fullWidth>
              <InputLabel>Strefa czasowa</InputLabel>
              <Select value={settings.timezone} onChange={handleChange('timezone')} label="Strefa czasowa">
                <MenuItem value="Europe/Warsaw">Europe/Warsaw</MenuItem>
                <MenuItem value="Europe/London">Europe/London</MenuItem>
                <MenuItem value="America/New_York">America/New York</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel>Język</InputLabel>
              <Select value={settings.language} onChange={handleChange('language')} label="Język">
                <MenuItem value="pl">Polski</MenuItem>
                <MenuItem value="en">English</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Button
            variant="contained"
            size="small"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={() => handleSave('org')}
            disabled={saving}
            sx={{ alignSelf: 'flex-start', borderRadius: `${theme.tokens?.primitive?.radius?.pill ?? 999}px` }}
          >
            Zapisz
          </Button>
        </Box>
      </SettingsSection>

      {/* Branding */}
      <SettingsSection title="Branding platformy">
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {/* Kolor podstawowy */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              component="label"
              htmlFor="brand-color-input"
              sx={{
                width: 40,
                height: 40,
                borderRadius: `${theme.tokens?.primitive?.radius?.control ?? 12}px`,
                bgcolor: settings.brandColor,
                flexShrink: 0,
                cursor: 'pointer',
                border: `1px solid ${theme.palette.divider}`,
              }}
            />
            <input
              id="brand-color-input"
              type="color"
              value={settings.brandColor}
              onChange={handleChange('brandColor')}
              style={{ position: 'absolute', opacity: 0, width: 0, height: 0, pointerEvents: 'none' }}
            />
            <TextField
              label="Kolor podstawowy (HEX)"
              value={settings.brandColor}
              onChange={handleChange('brandColor')}
              size="small"
              sx={{ maxWidth: 180 }}
              InputLabelProps={{ shrink: true }}
            />
          </Box>

          {/* Kolor akcentu */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              component="label"
              htmlFor="accent-color-input"
              sx={{
                width: 40,
                height: 40,
                borderRadius: `${theme.tokens?.primitive?.radius?.control ?? 12}px`,
                bgcolor: settings.accentColor,
                flexShrink: 0,
                cursor: 'pointer',
                border: `1px solid ${theme.palette.divider}`,
              }}
            />
            <input
              id="accent-color-input"
              type="color"
              value={settings.accentColor}
              onChange={handleChange('accentColor')}
              style={{ position: 'absolute', opacity: 0, width: 0, height: 0, pointerEvents: 'none' }}
            />
            <TextField
              label="Kolor akcentu (HEX)"
              value={settings.accentColor}
              onChange={handleChange('accentColor')}
              size="small"
              sx={{ maxWidth: 180 }}
              InputLabelProps={{ shrink: true }}
            />
          </Box>

          {/* Czcionka */}
          <TextField
            label="Nazwa czcionki (font-family)"
            value={settings.fontFamily}
            onChange={handleChange('fontFamily')}
            fullWidth
            size="small"
            placeholder="np. 'PKO Bankowe', sans-serif"
            helperText="Wpisz dokładną nazwę rodziny czcionki z pliku CSS."
          />
          <TextField
            label="URL do pliku CSS czcionki (opcjonalnie)"
            value={settings.fontUrl}
            onChange={handleChange('fontUrl')}
            fullWidth
            size="small"
            placeholder="https://www.pkobp.pl/content/dam/etc/fonts/styles.css"
            helperText="Podaj adres pliku CSS, który załaduje czcionkę z zewnętrznego serwera."
          />
          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Skala wielkości czcionki: <strong>{Math.round(settings.fontSizeScale * 100)}%</strong>
            </Typography>
            <Slider
              value={settings.fontSizeScale}
              onChange={(_, val) => setSettings((prev) => ({ ...prev, fontSizeScale: val }))}
              min={0.75}
              max={1.25}
              step={0.05}
              marks={[
                { value: 0.75, label: '75%' },
                { value: 1, label: '100%' },
                { value: 1.25, label: '125%' },
              ]}
              sx={{ maxWidth: 320 }}
            />
          </Box>

          {/* Slogan */}
          <TextField
            label="Slogan organizacji (opcjonalnie)"
            value={settings.slogan}
            onChange={handleChange('slogan')}
            fullWidth
            size="small"
            placeholder="np. Razem się ruszamy"
          />

          {/* Import stylu ze strony */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, p: 2, borderRadius: `${theme.tokens?.primitive?.radius?.card ?? 20}px`, border: `1px dashed ${theme.palette.divider}`, bgcolor: 'background.default' }}>
            <Typography variant="subtitle2" fontWeight={700}>Importuj styl ze strony</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                value={styleImportUrl}
                onChange={(e) => setStyleImportUrl(e.target.value)}
                placeholder="https://www.pkobp.pl"
                size="small"
                fullWidth
                label="Adres strony klienta"
                disabled={styleImportLoading}
              />
              <Button
                variant="contained"
                size="small"
                onClick={handleImportStyle}
                disabled={styleImportLoading || !styleImportUrl.trim()}
                sx={{ whiteSpace: 'nowrap', borderRadius: `${theme.tokens?.primitive?.radius?.pill ?? 999}px` }}
              >
                {styleImportLoading ? <CircularProgress size={16} color="inherit" /> : 'Pobierz styl'}
              </Button>
            </Box>
            {styleImportResult && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {styleImportResult.siteName && (
                  <Typography variant="body2" fontWeight={600}>
                    Wykryta nazwa: {styleImportResult.siteName}
                  </Typography>
                )}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                  {styleImportResult.colors?.map((color) => (
                    <Box key={color} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.5, borderRadius: `${theme.tokens?.primitive?.radius?.pill ?? 999}px`, border: `1px solid ${theme.palette.divider}` }}>
                      <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: color }} />
                      <Typography variant="caption">{color}</Typography>
                    </Box>
                  ))}
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Czcionki: {styleImportResult.fonts?.slice(0, 3).join(', ') || '—'}
                </Typography>
                {styleImportResult.logo && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box component="img" src={styleImportResult.logo} alt="Logo" sx={{ maxHeight: 32, maxWidth: 120, objectFit: 'contain' }} />
                    <Typography variant="caption" color="text.secondary">Znaleziono logo</Typography>
                  </Box>
                )}
                <Button variant="outlined" size="small" onClick={applyImportedStyle} sx={{ alignSelf: 'flex-start', borderRadius: `${theme.tokens?.primitive?.radius?.pill ?? 999}px` }}>
                  Zastosuj sugerowane style
                </Button>
              </Box>
            )}
          </Box>

          {/* Style validator */}
          <StyleValidator settings={settings} onApply={handleStyleAutoFix} />

          {/* Logo */}
          <Box>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>Logo organizacji (PNG / SVG)</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              {settings.logo ? (
                <Box
                  component="img"
                  src={settings.logo}
                  alt="Logo"
                  sx={{ maxHeight: 48, maxWidth: 160, objectFit: 'contain' }}
                />
              ) : (
                <Box
                  sx={{
                    width: 80,
                    height: 48,
                    borderRadius: `${theme.tokens?.primitive?.radius?.control ?? 12}px`,
                    border: `1px dashed ${theme.palette.divider}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'text.secondary',
                    fontSize: '0.75rem',
                  }}
                >
                  Brak logo
                </Box>
              )}
              <Button
                variant="outlined"
                size="small"
                component="label"
                startIcon={<UploadFileIcon />}
                disabled={saving}
                sx={{ borderRadius: `${theme.tokens?.primitive?.radius?.pill ?? 999}px` }}
              >
                Wybierz plik
                <input type="file" accept="image/png,image/svg+xml,image/jpeg" hidden onChange={handleLogoUpload} />
              </Button>
            </Box>
          </Box>

          <FormControlLabel
            control={<Switch checked={settings.showLogoInCampaigns} onChange={handleChange('showLogoInCampaigns')} />}
            label="Pokazuj logo organizacji w kampaniach"
          />
          <Button
            variant="contained"
            size="small"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={() => handleSave('branding')}
            disabled={saving}
            sx={{ alignSelf: 'flex-start', borderRadius: `${theme.tokens?.primitive?.radius?.pill ?? 999}px` }}
          >
            Zapisz branding
          </Button>
        </Box>
      </SettingsSection>

      {/* Prywatność */}
      <SettingsSection title="Prywatność i dane">
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Minimalna wielkość zespołu w rankingu: <strong>{settings.minTeamSize}</strong>
            </Typography>
            <Slider
              value={settings.minTeamSize}
              onChange={(_, val) => setSettings((prev) => ({ ...prev, minTeamSize: val }))}
              min={2}
              max={20}
              step={1}
              marks={[{ value: 5, label: '5' }, { value: 10, label: '10' }, { value: 15, label: '15' }]}
              sx={{ maxWidth: 320 }}
            />
          </Box>
          <FormControlLabel
            control={<Switch checked={settings.aliasMode} onChange={handleChange('aliasMode')} />}
            label="Tryb alias — ukryj prawdziwe imiona w rankingach publicznych"
          />
          <FormControl size="small" sx={{ maxWidth: 240 }}>
            <InputLabel>Retencja danych</InputLabel>
            <Select value={settings.dataRetentionMonths} onChange={handleChange('dataRetentionMonths')} label="Retencja danych">
              <MenuItem value={6}>6 miesięcy</MenuItem>
              <MenuItem value={12}>12 miesięcy</MenuItem>
              <MenuItem value={24}>24 miesiące</MenuItem>
              <MenuItem value={36}>36 miesięcy</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="contained"
            size="small"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={() => handleSave('privacy')}
            disabled={saving}
            sx={{ alignSelf: 'flex-start', borderRadius: `${theme.tokens?.primitive?.radius?.pill ?? 999}px` }}
          >
            Zapisz
          </Button>
        </Box>
      </SettingsSection>

      {/* Powiadomienia */}
      <SettingsSection title="Powiadomienia">
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FormControlLabel
            control={<Switch checked={settings.emailNotifications} onChange={handleChange('emailNotifications')} />}
            label="E-mail"
          />
          <FormControlLabel
            control={<Switch checked={settings.pushNotifications} onChange={handleChange('pushNotifications')} />}
            label="Push"
          />
          <FormControlLabel
            control={<Switch checked={settings.slackNotifications} onChange={handleChange('slackNotifications')} />}
            label="Slack"
          />
          <FormControl size="small" sx={{ maxWidth: 200, mt: 1 }}>
            <InputLabel>Częstotliwość</InputLabel>
            <Select value={settings.notificationFrequency} onChange={handleChange('notificationFrequency')} label="Częstotliwość">
              <MenuItem value="realtime">Na bieżąco</MenuItem>
              <MenuItem value="daily">Raz dziennie</MenuItem>
              <MenuItem value="weekly">Raz w tygodniu</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="contained"
            size="small"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={() => handleSave('notifications')}
            disabled={saving}
            sx={{ alignSelf: 'flex-start', borderRadius: `${theme.tokens?.primitive?.radius?.pill ?? 999}px`, mt: 1 }}
          >
            Zapisz
          </Button>
        </Box>
      </SettingsSection>

      {/* Członkowie zespołu HR */}
      <SettingsSection title="Zespół HR">
        <List disablePadding>
          {admins.map((admin, idx) => (
            <React.Fragment key={admin.id}>
              <ListItem
                secondaryAction={
                  <IconButton edge="end" size="small" onClick={() => setSnackbar({ open: true, message: 'Usuwanie użytkowników dostępne w zakładce Pracownicy', severity: 'info' })}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                }
                sx={{ px: 0 }}
              >
                <ListItemAvatar>
                  <Avatar sx={{ bgcolor: 'primary.light', color: 'primary.main', width: 36, height: 36, fontSize: '0.8rem', fontWeight: 700 }}>
                    {(admin.name || '').split(' ').map((n) => n[0]).join('').toUpperCase() || '?'}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{admin.name}</Typography>
                      <Chip label={admin.role} size="small" variant="outlined" />
                    </Box>
                  }
                  secondary={admin.email}
                />
              </ListItem>
              {idx < admins.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </List>
        <Button
          size="small"
          startIcon={<PersonAddIcon />}
          onClick={() => navigate('/employees')}
          sx={{ mt: 2, borderRadius: `${theme.tokens?.primitive?.radius?.pill ?? 999}px` }}
        >
          Zaproś nowego admina
        </Button>
      </SettingsSection>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Settings;
